import { Component, forwardRef, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, Validators, ValidationErrors, UntypedFormArray, ValidatorFn, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DeveloperDataFieldChoicesComponent } from '../developer-data-field-choices/developer-data-field-choices.component';
import { DeveloperDataFieldGroupsComponent } from '../developer-data-field-groups/developer-data-field-groups.component';
import { DataFieldChoice } from '../models/data-field';
import { DataFieldGroup } from '../models/data-field-group';
import { DataFieldType } from '../models/data-field-type';


@Component({
    selector: 'app-developer-data-field',
    templateUrl: './developer-data-field.component.html',
    styleUrls: ['./developer-data-field.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DeveloperDataFieldComponent),
            multi: true
        }, {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DeveloperDataFieldComponent),
            multi: true,
        }],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule]
})
export class DeveloperDataFieldComponent implements ControlValueAccessor, Validator, OnInit {
    public formData: UntypedFormGroup;

    @Input() dataFieldGroups: DataFieldGroup[];
    @Input() dataFieldTypes: DataFieldType[];

    @Input() groupTooltip: string;
    @Input() typeTooltip: string;
    @Input() displayNameTooltip: string;
    @Input() isRequiredTooltip: string;
    @Input() dataLengthTooltip: string;
    @Input() regexTooltip: string;
    @Input() regexTextTooltip: string;
    @Input() infoTextTooltip: string;
    @Input() defaultValueTooltip: string;
    @Input() sortOrderTooltip: string;
    @Input() choicesTooltip = 'Option to define custom values in the dropdown.';

    @Input() source: string;

    constructor(
        private fb: UntypedFormBuilder,
        private modalService: NgbModal) { }

    // This validator function calls the specified validator when the predicate returns true
    conditionalValidator(predicate: () => boolean, validator: ValidatorFn): ValidatorFn {
        return ((formControl: UntypedFormControl) => {
            if (!formControl.parent) {
                return null;
            }
            if (predicate()) {
                return validator(formControl);
            }
            return null;
        });
    }

    // Begin ControlValueAccessor //
    // Implement this so this component becomes a control on the parent form

    writeValue(value: any): void {
        if (value) {
            const choices = (value.choices as DataFieldChoice[]).sort((a, b) => a.SortOrder < b.SortOrder ? -1 : (a.SortOrder > b.SortOrder ? 1 : 0));
            this.formData = this.fb.group({
                dataFieldGroupsId: [value.dataFieldGroupsId, Validators.min(0)],
                dataTypeId: [value.dataTypeId, Validators.min(0)],
                displayName: [value.displayName, this.conditionalValidator(() => this.showField('displayName'), Validators.required)],
                isRequired: value.isRequired,
                dataLength: [value.dataLength, this.conditionalValidator(() => this.showField('dataLength'),
                    Validators.compose([Validators.required, Validators.min(1), Validators.max(4000)]))],
                regex: value.regex,
                regexText: value.regexText,
                infoText: value.infoText,
                defaultValue: value.defaultValue,
                sortOrder: [value.sortOrder, this.conditionalValidator(() => this.showField('sortOrder'),
                    Validators.compose([Validators.required, Validators.min(1)]))],
                choices: this.fb.array(
                    choices.map((e: DataFieldChoice) => this.fb.control(e))
                ),
            });

            this.formData.get('dataTypeId').valueChanges.subscribe(_ => {
                this.formData.get('displayName').updateValueAndValidity();
                this.formData.get('dataLength').updateValueAndValidity();
                this.formData.get('sortOrder').updateValueAndValidity();
            });

        }
    }

    registerOnChange(fn: any): void {
        this.formData.valueChanges.subscribe(fn);
    }

    onTouched: () => void = () => { };

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.formData.disable() : this.formData.enable();
    }

    // End ControlValueAccessor //
    // Begin Validator //
    // Implement this to pass validation status to parent form

    validate(_: AbstractControl): ValidationErrors | null {
        return this.formData.valid ? null : { 'app-developer-data-field': false };
    }

    registerOnValidatorChange(fn: () => void): void {
        this.formData.statusChanges.subscribe(fn);
    }

    // End Validator //

    ngOnInit(): void {
        this.formData = this.fb.group({
            dataFieldGroupsId: ['', Validators.min(0)],
            dataTypeId: ['', Validators.min(0)],
            displayName: ['', Validators.required],
            isRequired: '',
            dataLength: ['', [Validators.required, Validators.min(1), Validators.max(4000)]],
            regex: '',
            regexText: '',
            infoText: '',
            defaultValue: '',
            sortOrder: ['', [Validators.required, Validators.min(1)]],
            choices: this.fb.array([
            ]),
        });

        this.sortGroups();
    }

    sortGroups(): void {
        this.dataFieldGroups = this.dataFieldGroups
            .sort((a, b) => a.DataFieldGroupId === -1 ? -1 :
                b.DataFieldGroupId === -1 ? 1 :
                    a.DisplayName.localeCompare(b.DisplayName));
    }

    showField(field: string): boolean {
        const dataTypeId = this.formData.get('dataTypeId').value;
        const dataType = this.dataFieldTypes.find(e => e.DateTypeId === dataTypeId)?.DataTypeEnum;

        if (!dataType) {
            return false;
        }

        const checkBoxAndFilesFields = ['displayName', 'infoText', 'sortOrder'];
        const dropdownFields = ['displayName', 'infoText', 'sortOrder', 'choices'];
        const textBoxAndPasswordFields = ['displayName', 'dataLength', 'regex', 'regexText', 'infoText', 'defaultValue', 'sortOrder'];
        const labelFields = ['displayName', 'dataLength', 'regex', 'regexText', 'infoText', 'defaultValue', 'sortOrder', 'choices'];
        const isRequiredField = field === 'isRequired' && this.source === 'configurationKey';

        switch (dataType) {
            case 'CheckBox':
                return checkBoxAndFilesFields.includes(field);

            case 'File':
                return checkBoxAndFilesFields.includes(field) || isRequiredField;

            case 'DropDown':
                return dropdownFields.includes(field) || isRequiredField;

            case 'TextBox':
            case 'Password':
                return textBoxAndPasswordFields.includes(field) || isRequiredField;

            case 'Unknown': // Label
                return labelFields.includes(field) || isRequiredField;

            default:
                return true;
        }
    }

    get choices(): DataFieldChoice[] {
        const arr = this.formData.get('choices') as UntypedFormArray;
        return arr.value;
    }

    async onEditGroups(): Promise<void> {
        try {
            const modalRef = this.modalService.open(DeveloperDataFieldGroupsComponent, { backdrop: 'static', size: 'lg', centered: true });
            const result = await modalRef.result as DataFieldGroup[];
            if (result) {
                const none = this.dataFieldGroups.find(e => e.DataFieldGroupId === -1);
                this.dataFieldGroups = result;
                this.dataFieldGroups.push(none);
                this.sortGroups();
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
    }

    async onEditChoices(): Promise<void> {
        try {
            const choices = this.formData.get('choices') as UntypedFormArray;

            const modalRef = this.modalService.open(DeveloperDataFieldChoicesComponent, { backdrop: 'static', size: 'lg', centered: true });
            // Generate of clone of choice values so the dialog doesn't modify our form data
            modalRef.componentInstance.choices = choices.value.map(e => Object.assign({}, e));

            const result = await modalRef.result as DataFieldChoice[];
            if (result) {
                choices.clear();
                result
                    .sort((a, b) => a.SortOrder < b.SortOrder ? -1 : (a.SortOrder > b.SortOrder ? 1 : 0))
                    .map(e => {
                        choices.push(this.fb.control(e));
                    });
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
    }
}
